import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { BLOCK_SPACING, lookup } from '../fields/tokens';
import type { BlockSpacing } from '../fields/tokens';

/** `same` keeps the desktop height on mobile rather than overriding it. */
const MOBILE_INHERIT = 'same';

type MobileHeight = BlockSpacing | typeof MOBILE_INHERIT;

export type SpacerProps = {
  height: BlockSpacing;
  mobileHeight: MobileHeight;
};

/**
 * The mobile override falls back to the desktop variable, so leaving mobile on `same`
 * means no second value to keep in sync.
 */
const StyledSpacer = styled.div`
  height: var(--cms-spacer-height, ${BLOCK_SPACING.lg});

  @media ${mediaQueries.largePhone} {
    height: var(--cms-spacer-mobile-height, var(--cms-spacer-height, ${BLOCK_SPACING.lg}));
  }
`;

const HEIGHT_OPTIONS: { label: string; value: BlockSpacing }[] = [
  { label: 'Extra small', value: 'xs' },
  { label: 'Small', value: 'sm' },
  { label: 'Medium', value: 'md' },
  { label: 'Large', value: 'lg' },
  { label: 'Extra large', value: 'xl' },
  { label: '2X large', value: 'xxl' },
];

export const spacerConfig: ComponentConfig<SpacerProps> = {
  label: 'Spacer',
  ai: {
    instructions:
      'Invisible vertical space between components. Use to create breathing room between sections. Prefer a scale step over stacking multiple spacers.',
  },
  fields: {
    height: {
      type: 'select',
      label: 'Height',
      options: HEIGHT_OPTIONS,
      ai: {
        schema: { type: 'string', enum: HEIGHT_OPTIONS.map(({ value }) => value) },
        instructions: "Vertical space from the shared rhythm scale. 'lg' suits gaps between page sections.",
      },
    },
    mobileHeight: {
      type: 'select',
      label: 'Mobile height',
      options: [{ label: 'Same as desktop', value: MOBILE_INHERIT }, ...HEIGHT_OPTIONS],
      ai: {
        schema: { type: 'string', enum: [MOBILE_INHERIT, ...HEIGHT_OPTIONS.map(({ value }) => value)] },
        instructions:
          "Optional smaller height on phones, where large desktop gaps waste screen space. Leave as 'same' unless the desktop gap is xl or larger.",
      },
    },
  },
  defaultProps: {
    height: 'lg',
    mobileHeight: MOBILE_INHERIT,
  },
  render: ({ height = 'lg', mobileHeight = MOBILE_INHERIT }) => {
    const style: Record<string, string> = {
      '--cms-spacer-height': lookup(BLOCK_SPACING, height, BLOCK_SPACING.lg),
    };

    if (mobileHeight !== MOBILE_INHERIT) {
      style['--cms-spacer-mobile-height'] = lookup(BLOCK_SPACING, mobileHeight, BLOCK_SPACING.lg);
    }

    return <StyledSpacer aria-hidden='true' style={style as React.CSSProperties} />;
  },
};
